import { CalendarClock, Download, RefreshCw, Save, Search, Upload, Workflow } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const PayrollAttendanceToolbar = ({
  monthLabel,
  year,
  loading,
  canManagePayroll,
  attendanceSearchQuery,
  setAttendanceSearchQuery,
  attendanceSortField,
  setAttendanceSortField,
  attendanceSortDirection,
  setAttendanceSortDirection,
  attendanceStoreFilter,
  setAttendanceStoreFilter,
  attendanceEmployeeFilter,
  setAttendanceEmployeeFilter,
  attendanceIssueStatusFilter,
  setAttendanceIssueStatusFilter,
  activeAttendanceFiltersCount,
  locations,
  employees,
  pendingCount,
  validationCount,
  batchCount,
  attendanceFile,
  setAttendanceFile,
  attendanceImportMode,
  setAttendanceImportMode,
  attendanceUploading,
  onRefresh,
  onDownloadTemplate,
  onImportAttendance,
  onClearMonthData,
  onExportXlsx,
  onSaveCurrent,
  onOpenShiftDialog,
  saveActionLabel,
}) => {
  const storeOptions = locations.filter((location) => (location?.type ? location.type === 'store' : true) && location?.is_active !== false);

  return (
    <div
      className="sticky top-0 z-30 overflow-hidden rounded-md border border-slate-200 bg-white/95 shadow-sm backdrop-blur"
      data-testid="payroll-attendance-toolbar"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] px-4 py-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            <CalendarClock className="h-4 w-4 text-blue-600" />
            Controlo operacional de assiduidade
          </div>
          <h2 className="text-xl font-extrabold tracking-tight text-slate-950" data-testid="payroll-attendance-toolbar-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {monthLabel} {year}
          </h2>
          <p className="text-sm text-slate-600" data-testid="payroll-attendance-toolbar-subtitle">
            Matriz mensal compacta, justificação rápida, transferências e pendências accionáveis.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="payroll-attendance-toolbar-kpis">
          <Badge variant="outline" className="rounded-sm bg-white" data-testid="payroll-attendance-kpi-pending-open">Pendências: {pendingCount}</Badge>
          <Badge variant="outline" className="rounded-sm bg-orange-50 text-orange-700" data-testid="payroll-attendance-kpi-validation-open">Validações: {validationCount}</Badge>
          <Badge variant="outline" className="rounded-sm bg-indigo-50 text-indigo-700" data-testid="payroll-attendance-kpi-batches">Lotes: {batchCount}</Badge>
          {activeAttendanceFiltersCount > 0 && (
            <Badge variant="outline" className="rounded-sm bg-slate-100" data-testid="payroll-attendance-kpi-filters">Filtros: {activeAttendanceFiltersCount}</Badge>
          )}
        </div>
      </div>

      <div className="grid gap-2 px-4 py-3 xl:grid-cols-[minmax(0,1.25fr)_repeat(5,minmax(0,0.62fr))_auto] xl:items-end">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500" htmlFor="attendance-search">Pesquisar</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              id="attendance-search"
              value={attendanceSearchQuery}
              onChange={(event) => setAttendanceSearchQuery(event.target.value)}
              placeholder="Funcionário, loja, data ou conflito"
              className="h-9 rounded-sm border-slate-300 bg-slate-50 pl-8 text-sm"
              data-testid="payroll-attendance-search-input"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Loja</label>
          <Select value={attendanceStoreFilter} onValueChange={setAttendanceStoreFilter}>
            <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="payroll-attendance-filter-store">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {storeOptions.map((location) => (
                <SelectItem key={location.id || location.name} value={location.name}>{location.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Colaborador</label>
          <Select value={attendanceEmployeeFilter} onValueChange={setAttendanceEmployeeFilter}>
            <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="payroll-attendance-filter-employee">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {employees.slice(0, 200).map((employee) => (
                <SelectItem key={employee.id || employee.employee_number} value={employee.employee_number}>{employee.employee_number} · {employee.full_name || employee.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Pendência</label>
          <Select value={attendanceIssueStatusFilter} onValueChange={setAttendanceIssueStatusFilter}>
            <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="payroll-attendance-filter-issue-status">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="open">Abertas</SelectItem>
              <SelectItem value="error">Erro crítico</SelectItem>
              <SelectItem value="warning">Aviso</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Ordenar</label>
          <Select value={attendanceSortField} onValueChange={setAttendanceSortField}>
            <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="payroll-attendance-sort-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="employee">Funcionário</SelectItem>
              <SelectItem value="hours">Horas</SelectItem>
              <SelectItem value="location">Loja</SelectItem>
              <SelectItem value="status">Estado</SelectItem>
              <SelectItem value="date">Dias válidos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Direcção</label>
          <Select value={attendanceSortDirection} onValueChange={setAttendanceSortDirection}>
            <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="payroll-attendance-sort-direction">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">Asc.</SelectItem>
              <SelectItem value="desc">Desc.</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap items-end justify-end gap-2">
          <div className="min-w-[220px] flex-1 space-y-1 xl:min-w-[200px] xl:flex-none">
            <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500" htmlFor="attendance-import-input">Importação</label>
            <Input
              id="attendance-import-input"
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(event) => setAttendanceFile(event.target.files?.[0] || null)}
              className="h-9 rounded-sm text-sm file:mr-2 file:rounded-sm file:border-0 file:bg-slate-100 file:px-2 file:py-1 file:text-xs file:font-semibold"
              data-testid="payroll-attendance-import-input"
            />
          </div>

          <div className="min-w-[180px] space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Modo de importação</label>
            <Select value={attendanceImportMode} onValueChange={setAttendanceImportMode}>
              <SelectTrigger className="h-9 rounded-sm text-sm" data-testid="payroll-attendance-import-mode-select">
                <SelectValue placeholder="Escolher modo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Importar sem sobrepor</SelectItem>
                <SelectItem value="overwrite">Importar e sobrepor dados existentes</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm px-3" onClick={onOpenShiftDialog} data-testid="payroll-attendance-shifts-button">
              <Workflow className="mr-1.5 h-4 w-4" />
              Turnos
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm px-3" onClick={onRefresh} disabled={loading} data-testid="payroll-attendance-refresh-button">
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Actualizar
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm px-3" onClick={onDownloadTemplate} data-testid="payroll-attendance-template-button">
              <Download className="mr-1.5 h-4 w-4" />
              Template
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm px-3" onClick={onExportXlsx} data-testid="payroll-attendance-export-button">
              <Download className="mr-1.5 h-4 w-4" />
              Excel
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-9 rounded-sm border-red-200 px-3 text-red-700 hover:bg-red-50" onClick={onClearMonthData} disabled={!canManagePayroll} data-testid="payroll-attendance-clear-month-button">
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Limpar dados do mês
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-sm px-3"
              onClick={onImportAttendance}
              disabled={!attendanceFile || attendanceUploading || !canManagePayroll}
              data-testid="payroll-attendance-import-button"
            >
              <Upload className="mr-1.5 h-4 w-4" />
              {attendanceUploading ? 'A importar...' : 'Importar'}
            </Button>
            <Button type="button" size="sm" className="h-9 rounded-sm bg-slate-950 px-3 text-white hover:bg-slate-800" onClick={onSaveCurrent} disabled={!canManagePayroll} data-testid="payroll-attendance-save-button">
              <Save className="mr-1.5 h-4 w-4" />
              {saveActionLabel}
            </Button>
          </div>
        </div>
      </div>

      {attendanceFile && (
        <div className="border-t border-slate-200 px-4 py-2" data-testid="payroll-attendance-selected-file-row">
          <Badge variant="outline" className="rounded-sm bg-slate-50 text-slate-700" data-testid="payroll-attendance-selected-file-name">
            Ficheiro pronto: {attendanceFile.name}
          </Badge>
          <Badge variant="outline" className="ml-2 rounded-sm bg-white text-slate-700" data-testid="payroll-attendance-selected-import-mode">
            {attendanceImportMode === 'overwrite' ? 'Modo: sobrepor' : 'Modo: sem sobrepor'}
          </Badge>
        </div>
      )}
    </div>
  );
};